import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaBuilding,
  FaChartLine,
  FaCheckCircle,
  FaCodeBranch,
  FaDatabase,
  FaInstagram,
  FaLinkedin,
  FaLock,
  FaPlay,
  FaShieldAlt,
  FaUserShield,
  FaYoutube
} from 'react-icons/fa';
import { SiGoogle, SiHubspot, SiMailchimp, SiSlack, SiStripe, SiZapier } from 'react-icons/si';
import wordpressLogo from '../assets/images/platforms/wordpress.png';
import wixLogo from '../assets/images/platforms/wix.svg';
import squarespaceLogo from '../assets/images/platforms/squarespace.svg';
import webflowLogo from '../assets/images/platforms/webflow.svg';
import shopifyLogo from '../assets/images/platforms/shopify.svg';
import joomlaLogo from '../assets/images/platforms/joomla.svg';
import heroIllustration from '../assets/images/ils_14.svg';
import card1Image from '../assets/images/ils_14.3.svg';
import card2Image from '../assets/images/ils_14.2.svg';
import card3Image from '../assets/images/40.svg';
import decorRight from '../assets/images/103.svg';
import decorLeft from '../assets/images/102.svg';
import featuresLeft from '../assets/images/113.svg';
import manageTestimonialsImage from '../assets/images/my-testimonials.png';
import step1Image from '../assets/images/Join-us1.png';
import step2Image from '../assets/images/share-record2.png';
import step3Image from '../assets/images/showcase-reviews.png';
import {
  trackLoginClick,
  trackStartFree,
  trackWatchDemo,
  useVideoAnalytics,
  useYouTubeIframeAnalytics,
  withYouTubeAnalyticsParams
} from '../utils/analytics.js';
import { getPublishedBlogs, slugify } from '../utils/blogApi.js';
import { setPageSEO } from '../utils/seo.js';
import './Home.css';

const signupUrl = '/signup';
const demoVideoSrc = '/vedio/everyclientalio.mp4';
const bwaysUrl = 'https://bwaystechnosolution.co.in/';
const youtubeUrl = 'https://www.youtube.com/@clientalio';
const instagramUrl = 'https://www.instagram.com/clientalio/';
const linkedinUrl = 'https://www.linkedin.com/company/bways-techno-solution/';
const heroActions = ['Collect', 'Manage', 'Publish'];

const fadeInUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 }
};

const staggerChildren = {
  visible: {
    transition: {
      staggerChildren: 0.09
    }
  }
};

function formatBlogDate(value) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
}

function AnimatedSection({ children, className, id }) {
  return (
    <motion.section
      id={id}
      className={className}
      variants={fadeInUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.55, ease: 'easeOut' }}
    >
      {children}
    </motion.section>
  );
}

function Counter({ end, suffix = '', duration = 1200 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return undefined;

    const startTime = performance.now();
    const step = (currentTime) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
    return undefined;
  }, [duration, end, isInView]);

  return <span ref={ref}>{count}{suffix}</span>;
}

function YouTubeTrackedIframe({ src, title }) {
  const iframeRef = useYouTubeIframeAnalytics(title);

  return (
    <iframe
      ref={iframeRef}
      title={title}
      src={withYouTubeAnalyticsParams(src)}
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
    ></iframe>
  );
}

function Home() {
  const [activeFeature, setActiveFeature] = useState(0);
  const [heroActionIndex, setHeroActionIndex] = useState(0);
  const [email, setEmail] = useState('');
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [isLoadingBlogs, setIsLoadingBlogs] = useState(true);
  const [showHeroPlayer, setShowHeroPlayer] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [playingIndex, setPlayingIndex] = useState(null);
  const heroVideoAnalytics = useVideoAnalytics('Clientalio demo hero video');
  const demoModalVideoAnalytics = useVideoAnalytics('Clientalio demo modal video');

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setHeroActionIndex((currentIndex) => (currentIndex + 1) % heroActions.length);
    }, 2200);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => setPageSEO({
    title: 'Clientalio - Turn Customer Feedback Into Trust',
    description: 'Turn happy customers into trust, credibility, and social proof that helps more buyers choose your business.',
    canonical: 'https://clientalio.com/',
    ogTitle: 'Clientalio - Turn Customer Feedback Into Trust',
    ogDescription: 'Make customer love visible and give buyers a reason to trust you before they talk to you.',
    twitterTitle: 'Clientalio - Turn Customer Feedback Into Trust',
    twitterDescription: 'Turn happy customers into trust, credibility, and social proof that helps more buyers choose your business.'
  }), []);

  useEffect(() => {
    let isMounted = true;

    async function loadLatestBlogs() {
      setIsLoadingBlogs(true);

      try {
        const blogItems = await getPublishedBlogs({ pageSize: 3 });

        if (isMounted) {
          setLatestBlogs(blogItems.slice(0, 3));
        }
      } catch {
        if (isMounted) {
          setLatestBlogs([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingBlogs(false);
        }
      }
    }

    loadLatestBlogs();

    return () => {
      isMounted = false;
    };
  }, []);

  const getYoutubeId = (url) => {
    if (!url) return null;
    const embedMatch = url.match(/embed\/([a-zA-Z0-9_-]+)/);
    if (embedMatch) return embedMatch[1];
    const vMatch = url.match(/[?&]v=([^&]+)/);
    if (vMatch) return vMatch[1];
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) return shortMatch[1];
    return null;
  };


  const steps = [
    {
      image: step1Image,
      title: 'Create your trust page',
      description: 'Start with Google and set up a branded page for customer feedback.'
    },
    {
      image: step2Image,
      title: 'Ask at the right moment',
      description: 'Send one simple link when your customer is happiest.'
    },
    {
      image: step3Image,
      title: 'Make customer love visible',
      description: 'Add approved customer proof to the places buyers make decisions.'
    }
  ];

  const features = [
    {
      image: step2Image,
      title: 'Build trust before buyers contact you',
      description: 'Invite customers with one simple link and turn their feedback into proof new buyers can believe.'
    },
    {
      image: manageTestimonialsImage,
      title: 'Stop losing valuable customer feedback',
      description: 'Approve, organize, and reuse customer stories before they get buried in emails, forms, and chats.'
    },
    {
      image: step3Image,
      title: 'Turn customer love into conversions',
      description: 'Place approved proof on landing pages, websites, and campaigns where trust can move buyers forward.'
    }
  ];

  const integrations = [
    { name: 'WordPress', logo: wordpressLogo },
    { name: 'Wix', logo: wixLogo },
    { name: 'Squarespace', logo: squarespaceLogo },
     { name: 'Shopify', logo: shopifyLogo },
    { name: 'Joomla', logo: joomlaLogo },
    { name: 'HubSpot', icon: SiHubspot },
    { name: 'Mailchimp', icon: SiMailchimp },
    { name: 'Webflow', logo: webflowLogo },
    { name: 'Zapier', icon: SiZapier },
    { name: 'Slack', icon: SiSlack },
  ];

  const trustedBy = [
    { label: 'Built by BWays Techno Solution', icon: <FaBuilding />, href: bwaysUrl },
    { label: 'YouTube', icon: <FaYoutube />, href: youtubeUrl },
    { label: 'Instagram', icon: <FaInstagram />, href: instagramUrl },
    { label: 'LinkedIn', icon: <FaLinkedin />, href: linkedinUrl },
    { label: 'Active Product Development', icon: <FaCodeBranch />, href: bwaysUrl }
  ];

 const tutorials = [
  {
    title: 'Collect & Create Customer Testimonials',
    src: 'https://www.youtube.com/embed/Iu6pdv-rTiY',
    href: 'https://www.youtube.com/watch?v=Iu6pdv-rTiY'
  },
  {
    title: 'Embed Customer Testimonials',
    src: 'https://www.youtube.com/embed/8t40_nLY544',
    href: 'https://www.youtube.com/watch?v=8t40_nLY544'
  },
  {
    title: 'Analytics Dashboard',
    src: 'https://www.youtube.com/embed/ANpbNmEzmyE',
    href: 'https://www.youtube.com/watch?v=ANpbNmEzmyE'
  }
];

  const communityCards = [
    {
      title: 'Latest YouTube videos',
      description: 'Tutorials, setup walkthroughs, and product updates from the Clientalio team.',
      icon: <FaYoutube />, 
      iconClass: 'youtube',
      href: youtubeUrl
    },
    {
      title: 'Latest Instagram reels',
      description: 'Short product tips, behind-the-scenes updates, and customer proof ideas.',
      icon: <FaInstagram />, 
      iconClass: 'instagram',
      href: instagramUrl
    },
    {
      title: 'Latest LinkedIn posts from BWays Techno Solution',
      description: 'Enterprise engineering updates and product development announcements.',
      icon: <FaLinkedin />, 
      iconClass: 'linkedin',
      href: linkedinUrl
    }
  ];

  const comparisonRows = [
    ['Customer proof before sales calls', true, false],
    ['Branded feedback experience', true, true],
    ['Conversion insight', true, false],
    ['Website-ready trust signals', true, false],
    ['Customer love page', true, false],
    ['CMS-ready publishing', true, false],
    ['Automated follow-up workflow', true, false]
  ];

  const securityBadges = [
    { label: 'SSL Secure', icon: <FaLock /> },
    { label: 'Privacy First', icon: <FaUserShield /> },
    { label: 'GDPR Ready', icon: <FaShieldAlt /> },
    { label: 'Encrypted Storage', icon: <FaDatabase /> }
  ];

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      setTimeout(() => {
        const section = document.getElementById(hash);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }

    const timer = setTimeout(() => {
      const wallContainer = document.getElementById('walloflove-widget-container');
      if (!wallContainer) return;

      wallContainer.innerHTML = '';
      const script = document.createElement('script');
      script.src = 'https://apiclientalio.azurewebsites.net/widgets/wall-of-love-embed.js';
      script.async = true;
      script.dataset.id = 'ff64d690-ed48-473f-a4ef-51c9e0993828';
      script.dataset.displayStyle = 'grid';
      script.dataset.rotate = 'false';
      script.dataset.title = 'What Our Customers Say';
      script.dataset.subtitle = 'Real feedback from agencies, freelancers, and businesses using Clientalio to collect and showcase testimonials.';
      script.dataset.showTitle = 'true';
      script.dataset.showSubtitle = 'true';
      script.dataset.theme = 'light';
      script.dataset.maxViewCards = '3';
      script.dataset.interval = '3000';
      script.dataset.enableAnimation = 'true';
      script.dataset.animationIcon = 'heart';
      script.dataset.animationDirection = 'down-top';
      script.dataset.design = 'Classic_default';
      wallContainer.appendChild(script);
    }, 100);

    return () => {
      clearTimeout(timer);
      const testimonialContainer = document.getElementById('testimonial-widget-container');
      const wallContainer = document.getElementById('walloflove-widget-container');
      if (testimonialContainer) testimonialContainer.innerHTML = '';
      if (wallContainer) wallContainer.innerHTML = '';
    };
  }, []);

  useEffect(() => {
    if (!showDemoModal) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setShowDemoModal(false);
      }
    };

    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [showDemoModal]);

  return (
    <div className="home">
      <section className="hero-section">
        <img src={decorLeft} alt="" className="hero-decor-left" />
        <img src={decorRight} alt="" className="hero-decor-right" />
        <div className="container">
          <div className="hero-content">
            <motion.div
              className="hero-text"
              variants={staggerChildren}
              initial="hidden"
              animate="visible"
            >
              <motion.h1 variants={fadeInUp} transition={{ duration: 0.55 }}>
                <span className="sr-only">Collect, manage, and publish customer testimonials with Clientalio.</span>
                <span aria-hidden="true">
                  <span className="hero-action-word">
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={heroActions[heroActionIndex]}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -18 }}
                        transition={{ duration: 0.28, ease: 'easeOut' }}
                      >
                        {heroActions[heroActionIndex]}
                      </motion.span>
                    </AnimatePresence>
                  </span>{' '}customer testimonials with Clientalio.
                </span>
              </motion.h1>
              <motion.p variants={fadeInUp} transition={{ duration: 0.55 }}>Turn real customer stories into social proof that builds trust and drives more conversions.</motion.p>
              <motion.div className="hero-actions" variants={fadeInUp} transition={{ duration: 0.55 }}>
                <motion.a whileHover={{ y: -3 }} whileTap={{ scale: 0.98 }} className="btn-primary" href={signupUrl} onClick={() => trackStartFree('home_hero')}>Start FREE In Under 2 Minutes</motion.a>
                <span className="hero-action-divider">or</span>
                <motion.button whileHover={{ y: -3 }} whileTap={{ scale: 0.98 }} className="btn-secondary hero-secondary" type="button" onClick={() => { trackWatchDemo('home_hero'); setShowDemoModal(true); }}>Watch 30 Sec Demo</motion.button>
              </motion.div>
            </motion.div>
            <motion.div
              className="hero-illustration"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.65, ease: 'easeOut' }}
            >
              <div className="hero-video">
                {!showHeroPlayer ? (
                  <div
                    className="video-poster"
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowHeroPlayer(true)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowHeroPlayer(true); }}
                    aria-label="Play demo video"
                  >
                    <video src={demoVideoSrc} preload="metadata" muted playsInline aria-label="Clientalio demo preview" />
                    <button className="custom-play-btn" aria-hidden>
                      <FaPlay />
                    </button>
                  </div>
                ) : (
                  <video
                    title="Clientalio demo"
                    src={demoVideoSrc}
                    autoPlay
                    controls
                    playsInline
                    {...heroVideoAnalytics}
                  ></video>
                )}
              </div>
            </motion.div>
          </div>
          <motion.ul className="hero-trust-list" variants={fadeInUp} initial="hidden" animate="visible" transition={{ duration: 0.55 }}>
            <li>✓ Create your branded trust page.</li>
            <li>✓ Share your first customer request.</li>
            <li>✓ Ready in under 2 minutes.</li>
            <li>✓ No Credit Card Required.</li>
            <li>✓ FREE During Beta.</li>
          </motion.ul>
          <motion.div
            className="hero-integrations"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.55, delay: 0.1 }}
          >
            <p className="hero-integrations-title">
              <span>Integrations</span>
              <strong>with</strong>
            </p>
            <div className="hero-integration-marquee" aria-label="Supported website platforms">
              <div className="hero-integration-logos">
                {[...integrations, ...integrations].map((integration, index) => {
                  const IntegrationIcon = integration.icon;

                  return (
                    <Link
                      to="/integration"
                      key={`${integration.name}-${index}`}
                      aria-label={index < integrations.length ? `Integrate Clientalio with ${integration.name}` : undefined}
                      aria-hidden={index >= integrations.length}
                      tabIndex={index >= integrations.length ? -1 : undefined}
                    >
                      {integration.logo ? (
                        <img src={integration.logo} alt={integration.name} />
                      ) : (
                        <IntegrationIcon title={integration.name} />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* <AnimatedSection className="trusted-by-section">
        <div className="container">
          <div className="trusted-by-grid">
            {trustedBy.map((item) => (
              <motion.a
                key={item.label}
                href={item.href}
                className="trusted-by-item"
                target="_blank"
                rel="noreferrer"
                whileHover={{ y: -4 }}
              >
                <span className="trusted-by-icon">{item.icon}</span>
                <span>{item.label}</span>
              </motion.a>
            ))}
          </div>
        </div>
      </AnimatedSection> */}

      <AnimatedSection className="steps-section">
        <div className="container">
          <h3 className="section-badge">GET STARTED IN MINUTES</h3>
          <h2 className="section-title"><span className="highlight">In 3 simple steps, turn customer love into your next customer.</span></h2>
          <div className="steps-grid">
            {steps.map((step, index) => (
              <motion.div key={step.title} className="step-card" whileHover={{ y: -6 }} transition={{ type: 'spring', stiffness: 260, damping: 20 }}>
                <div className="step-image">
                  <img src={step.image} alt={step.title} />
                </div>
                <h3>{step.title}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="clientalio-action" className="action-section">
        <div className="container">
          <h3 className="section-badge">SEE CLIENTALIO IN ACTION</h3>
          <h2 className="section-title"><span className="highlight">See how fast customer proof becomes a marketing asset.</span></h2>
          <div className="video-grid">
            {tutorials.map((tutorial, index) => {
              const vid = getYoutubeId(tutorial.src || tutorial.href);
              const thumb = vid ? `https://img.youtube.com/vi/${vid}/hqdefault.jpg` : '';
              return (
                <motion.div key={tutorial.title} className="video-card" whileHover={{ y: -6 }}>
                  <div className="video-frame">
                    {playingIndex === index ? (
                      <YouTubeTrackedIframe
                        title={tutorial.title}
                        src={`${tutorial.src}?autoplay=1&controls=0&rel=0&modestbranding=1`}
                      />
                    ) : (
                      <div
                        className="video-poster"
                        role="button"
                        tabIndex={0}
                        onClick={() => setPlayingIndex(index)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setPlayingIndex(index); }}
                        aria-label={`Play ${tutorial.title}`}
                      >
                        <img src={thumb} alt={`${tutorial.title} thumbnail`} />
                        <button className="custom-play-btn" aria-hidden>
                          <FaPlay />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="video-card-footer">
                    <span>{tutorial.title}</span>
                    <FaPlay />
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="section-button-wrap">
            <motion.a whileHover={{ y: -3 }} className="btn-primary" href={youtubeUrl} target="_blank" rel="noreferrer">View All Tutorials</motion.a>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="features" className="features-section">
        <div className="container">
          <h3 className="section-badge">WHY IT MATTERS</h3>
          <h2 className="section-title"> <span className="highlight">Your best marketing is already sitting with your customers</span></h2>
          <div className="features-content">
            <div className="features-accordion">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className={`accordion-item ${activeFeature === index ? 'active' : ''}`}
                  onClick={() => setActiveFeature(index)}
                  whileHover={{ x: 6 }}
                  transition={{ type: 'spring', stiffness: 280, damping: 22 }}
                >
                  <div className="accordion-header">
                    <h3>{feature.title}</h3>
                    <span className="accordion-icon">{activeFeature === index ? '−' : '+'}</span>
                  </div>
                  {activeFeature === index && (
                    <motion.div className="accordion-content" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                      <p>{feature.description}</p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
            <motion.div className="features-image" whileHover={{ y: -5 }}>
              <img src={features[activeFeature].image} alt={`${features[activeFeature].title} illustration`} />
              <img src={featuresLeft} alt="" className="features-decor" />
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="integration" className="integration-section">
        <div className="container">
          <h3 className="section-badge">PUBLISH WHERE BUYERS DECIDE</h3>
          <h2 className="section-title"> <span className="highlight">Make customer proof visible on your website</span> 🏆</h2>
          <div className="integration-grid">
            {integrations.map((integration) => (
              <motion.div key={integration.name} className="integration-card" whileHover={{ y: -6 }}>
                <div className="integration-icon">
                  {integration.logo ? (
                    <img src={integration.logo} alt={`${integration.name} logo`} />
                  ) : (
                    <integration.icon title={`${integration.name} logo`} />
                  )}
                </div>
                <p>{integration.name}</p>
              </motion.div>
            ))}
          </div>
            <motion.p className="coming-soon-text" animate={{ opacity: [0.65, 1, 0.65] }} transition={{ duration: 2.4, repeat: Infinity }}>
              20+ more integrations.
          </motion.p>
        </div>
      </AnimatedSection>

      <AnimatedSection className="community-section">
        <div className="container">
          <h3 className="section-badge">JOIN OUR COMMUNITY</h3>
          <h2 className="section-title"><span className="highlight">Follow practical ideas on trust, growth, and customer psychology.</span></h2>
          <div className="community-grid">
            {communityCards.map((card) => (
              <motion.a key={card.title} href={card.href} target="_blank" rel="noreferrer" className="community-card" whileHover={{ y: -6 }}>
                <span className={`community-icon ${card.iconClass}`}>{card.icon}</span>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="testimonials" className="client-say-section">
        <div className="container">
          {/* <h2 className="section-title">Check what's our client<br />say about us.</h2> */}
          <div className="testimonial-widget-container-bordered">
            <div className="walloflove-heart-animation" aria-hidden="true">
              <span>&hearts;</span>
              <span>&hearts;</span>
              <span>&hearts;</span>
              <span>&hearts;</span>
              <span>&hearts;</span>
              <span>&hearts;</span>
              <span>&hearts;</span>
              <span>&hearts;</span>
              <span>&hearts;</span>
              <span>&hearts;</span>
              <span>&hearts;</span>
              <span>&hearts;</span>
            </div>
            <div id="walloflove-widget-container" className="walloflove-widget-preview" aria-live="polite" />
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection className="home-blog-section">
        <div className="container">
          <h3 className="section-badge">CLIENTALIO BLOG</h3>
          <h2 className="section-title"><span className="highlight">Latest ideas from our blog.</span></h2>

          {isLoadingBlogs && <p className="home-blog-status">Loading latest blogs...</p>}

          {!isLoadingBlogs && latestBlogs.length > 0 && (
            <div className="home-blog-grid">
              {latestBlogs.map((blog) => {
                const blogPath = `/${blog.contentTypeSlug || slugify(blog.contentType || blog.category)}/${blog.slug}`;

                return (
                  <motion.article key={blog.id || blog.slug} className="home-blog-card" whileHover={{ y: -6 }}>
                    {blog.imageUrl && <img src={blog.imageUrl} alt="" className="home-blog-image" loading="lazy" />}
                    <div className="home-blog-card-body">
                      <div className="home-blog-meta">
                        {(blog.contentType || blog.category) && <span>{blog.contentType || blog.category}</span>}
                        {formatBlogDate(blog.publishedAt) && <time dateTime={blog.publishedAt}>{formatBlogDate(blog.publishedAt)}</time>}
                      </div>
                      <h3>{blog.title}</h3>
                      {blog.excerpt && <p>{blog.excerpt}</p>}
                      <Link to={blogPath} className="home-blog-read-link">Read article</Link>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          )}

          {!isLoadingBlogs && latestBlogs.length === 0 && (
            <p className="home-blog-status">Latest blog posts will appear here soon.</p>
          )}

          <div className="section-button-wrap">
            <Link className="btn-primary" to="/blog">View Blog</Link>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection className="comparison-section">
        <div className="container">
          <h3 className="section-badge">COMPARE THE WORKFLOW</h3>
          <h2 className="section-title"><span className="highlight">Clientalio vs doing nothing with customer feedback</span></h2>
          <div className="comparison-table-wrap">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Growth Outcome</th>
                  <th>Clientalio</th>
                  <th>Manual Follow-up</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map(([label, clientalio, manual]) => (
                  <tr key={label}>
                    <td>{label}</td>
                    <td className="included">{clientalio ? '✓' : '—'}</td>
                    <td>{manual ? '✓' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection className="security-section">
        <div className="container">
          <div className="security-badges">
            {securityBadges.map((badge) => (
              <motion.div key={badge.label} className="security-badge" whileHover={{ y: -4 }}>
                {badge.icon}
                <span>{badge.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection className="cta-section">
        <div className="container">
          <h2 className="section-title">Start building trust today</h2>
          <p className="section-subtitle">In less than 2 minutes, create your branded page, share your first link, and make customer love visible.</p>
          <div className="cta-actions">
            <motion.a whileHover={{ y: -3 }} whileTap={{ scale: 0.98 }} href={`${signupUrl}${email ? `?email=${encodeURIComponent(email)}` : ''}`} className="btn-primary" target="_blank" rel="noreferrer" onClick={() => trackStartFree('home_cta')}>Start Building Trust Today</motion.a>
            <motion.button whileHover={{ y: -3 }} whileTap={{ scale: 0.98 }} type="button" onClick={() => { trackWatchDemo('home_cta'); setShowDemoModal(true); }} className="btn-secondary">Watch Demo</motion.button>
          </div>
          <div className="cta-form">
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <a
              href={`${signupUrl}${email ? `?email=${encodeURIComponent(email)}` : ''}`}
              className="btn-primary"
              target="_blank"
              rel="noreferrer"
            >
              Start building trust
            </a>
          </div>
          <ul className="cta-proof-list">
            <li>✓ Free During Beta</li>
            <li>✓ No Credit Card Required</li>
            <li>✓ Cancel Anytime</li>
          </ul>
          <p className="cta-terms">
            Already a member? <Link to="/signup" onClick={() => trackLoginClick('home_cta')}>Sign in. </Link>
          </p>
        </div>
      </AnimatedSection>

      {/* <AnimatedSection className="credibility-section">
        <div className="container credibility-grid">
          <div>
            <h3 className="section-badge">BUILT BY BWays TECHNO SOLUTION</h3>
            <h2 className="section-title"><span className="highlight">Built by BWays Techno Solution</span></h2>
            <p>20+ Years of Enterprise Software Engineering delivering enterprise-grade software solutions across global markets.</p>
          </div>
          <div className="credibility-stats">
            <div><FaChartLine /><strong><Counter end={20} suffix="+" /></strong><span>Years of engineering</span></div>
            <div><FaShieldAlt /><strong>Global</strong><span>enterprise delivery</span></div>
          </div>
          <motion.a whileHover={{ y: -3 }} href={bwaysUrl} target="_blank" rel="noreferrer" className="btn-primary">Learn More About BWays</motion.a>
        </div>
      </AnimatedSection> */}

      {showDemoModal && (
        <div className="demo-modal" role="dialog" aria-modal="true" aria-label="Clientalio demo video">
          <button className="demo-modal-backdrop" type="button" aria-label="Close demo video" onClick={() => setShowDemoModal(false)} />
          <div className="demo-modal-content">
            <button className="demo-modal-close" type="button" aria-label="Close demo video" onClick={() => setShowDemoModal(false)}>×</button>
            <video
              title="Clientalio demo video"
              src={demoVideoSrc}
              autoPlay
              controls
              playsInline
              {...demoModalVideoAnalytics}
            ></video>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
